/**
 * 경고창 부품 (기반 공사 5단계, 공통/데이터원칙 0-5, 0-7. 2026-11-02, 2026-11-04 대표님)
 *
 * 세 가지 모양만 둔다. 디자인은 하지 않고 단순 화면으로(공통/작업규칙 9절).
 * 1. 막는 창: 계속 막는 것(같은 사람 중복, 만 15세 미만, 상한 넘는 소개요금, 틀린 주민번호와 사업자번호). 버튼은 "고치러 가기" 하나.
 * 2. 묻는 창: "무엇이 이상한지 + 그래도 할까요? + 그대로 하면 생기는 일". "고치러 가기"(남색, 대개 고를 쪽)와 "그래도 하기"(흰색).
 *    예: "유효하지 않은 체류기간이에요. 그래도 할까요? 제대로 서류가 만들어지지 않아요."
 * 3. 받는 창: 필요할 때 칸이 비었을 때 그 자리에서 받고 하던 일을 이어 간다(0-5 표). 안에 넣을 입력칸은 부르는 쪽이 준다.
 *
 * 쓰는 법: const dialog = useDialog(); ... if (await dialog.ask({ problem, result }) === 'go') { ... }  화면 어딘가에 {dialog.element}
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

export type AskAnswer = 'fix' | 'go';

interface Open {
  kind: '막음' | '물음' | '받음';
  problem: string;
  result?: string;
  body?: ReactNode;
  primary: string;
  secondary?: string;
  resolve: (answer: AskAnswer) => void;
}

const BUTTON = { fontSize: 20, padding: '12px 20px', margin: '0 8px 0 0', borderRadius: 6, cursor: 'pointer', border: '1px solid #1A202B' } as const;

export function useDialog() {
  const [open, setOpen] = useState<Open | null>(null);

  const show = useCallback((o: Omit<Open, 'resolve'>) => new Promise<AskAnswer>((resolve) => {
    setOpen({ ...o, resolve });
  }), []);

  /** 막는 창: 고치러 가기 하나 */
  const block = useCallback((problem: string) => show({ kind: '막음', problem, primary: '고치러 가기' }).then(() => undefined), [show]);

  /** 묻는 창: 그대로 하면 생기는 일(result)을 함께 적는다 */
  const ask = useCallback((p: { problem: string; result: string }) =>
    show({ kind: '물음', problem: p.problem, result: p.result, primary: '고치러 가기', secondary: '그래도 하기' }), [show]);

  /** 받는 창: 입력칸(body)을 보여 주고, 저장하면 'go', 그만두면 'fix' */
  const need = useCallback((p: { problem: string; body: ReactNode; save?: string; later?: string }) =>
    show({ kind: '받음', problem: p.problem, body: p.body, primary: p.save ?? '저장하고 이어서 하기', ...(p.later ? { secondary: p.later } : {}) }), [show]);

  const close = (answer: AskAnswer) => {
    open?.resolve(answer);
    setOpen(null);
  };

  const element = open ? <DialogView open={open} onAnswer={close} /> : null;
  return { block, ask, need, element };
}

function DialogView({ open, onAnswer }: { open: Open; onAnswer: (a: AskAnswer) => void }) {
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => { first.current?.focus(); }, []);
  // 받는 창의 주 버튼은 저장(go), 막는 창과 묻는 창의 주 버튼은 고치러 가기(fix)
  const primaryAnswer: AskAnswer = open.kind === '받음' ? 'go' : 'fix';
  const secondaryAnswer: AskAnswer = open.kind === '받음' ? 'fix' : 'go';
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={open.problem}
        data-dialog={open.kind}
        style={{ background: '#fff', borderRadius: 8, padding: 24, maxWidth: 520, width: '90%', fontSize: 20, lineHeight: 1.6 }}
      >
        <p style={{ margin: '0 0 12px', fontWeight: 700 }}>{open.problem}</p>
        {open.kind === '물음' && (
          <p style={{ margin: '0 0 16px' }}>그래도 할까요? {open.result}</p>
        )}
        {open.body && <div style={{ margin: '0 0 16px' }}>{open.body}</div>}
        <div>
          <button
            ref={first}
            type="button"
            data-usage={open.primary}
            onClick={() => onAnswer(primaryAnswer)}
            style={{ ...BUTTON, background: '#1A202B', color: '#fff' }}
          >
            {open.primary}
          </button>
          {open.secondary && (
            <button
              type="button"
              data-usage={open.secondary}
              onClick={() => onAnswer(secondaryAnswer)}
              style={{ ...BUTTON, background: '#fff', color: '#1A202B' }}
            >
              {open.secondary}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
